import type { BetType } from '@mobi/api-types/racePage/types'
import { timed } from '@core/Utils'
import * as EventAggregator from '../../AppUtils/Framework/Messaging/EventAggregator'
import { ModelSorter } from '../Sorting/ModelSorter'
import type { SortOption } from '../Sorting/types'
import type BetTypeInformation from './Betting/BetTypeInformation'
import BetTypeInformationBuilder from './Betting/BetTypeInformationBuilder'
import type { BettingInformation } from './BettingInformation'
import type ObservableRacePage from './Observables/ObservableRacePage'

export default class BettingInformationManager {
  constructor(raceInformation: ObservableRacePage, bettingInformation: BettingInformation) {
    this.raceInformation = raceInformation
    this.bettingInformation = bettingInformation
    this.reapplySort()
  }

  private setSortFor({ property, direction }: SortOption) {
    if (this.bettingInformation.sortOption != null) {
      this.bettingInformation.sortOption.property = property
      this.bettingInformation.sortOption.direction = direction
    }
  }

  private reapplySort() {
    const sortOption = this.bettingInformation.sortOption
    const startersForRace = this.raceInformation.getStartersForRace(
      this.raceInformation.meetingInformation.selectedRace.raceNumber()
    )

    if (startersForRace().length > 0 && this.bettingInformation.sortOption != null) {
      const isFixedAvailable =
        this.raceInformation.meetingInformation.selectedRace.fixedOddsInfo?.isRaceAvailable()

      if (
        !this.bettingInformation.selectedBetType().supportsSort(sortOption.property) ||
        (!isFixedAvailable && sortOption.property === 'FixedPrice')
      ) {
        this.bettingInformation.sortOption.property = 'Number'
        this.bettingInformation.sortOption.direction = 'Ascending'
      }
      ModelSorter.sortBy(startersForRace, sortOption)
    }
  }

  public processSort(params: { raceNumber: number; option: SortOption }) {
    ModelSorter.sortBy(this.raceInformation.getStartersForRace(params.raceNumber), params.option)
    this.setSortFor(params.option)
  }

  public update(raceChanged: boolean) {
    this.bettingInformation.mergeDone(false)

    const shouldResetToWinPlace = this.shouldResetBackToWinPlace()
    if (raceChanged || shouldResetToWinPlace) {
      if (shouldResetToWinPlace) this.bettingInformation.lastSelectionBetType(null)

      const betTypeToReset = shouldResetToWinPlace
        ? 'WinPlace'
        : this.bettingInformation.lastSelectionBetType() || 'WinPlace'

      this.reset(betTypeToReset, false)
    }
    this.reapplySort()

    this.bettingInformation.mergeDone(true)
  }

  public processBetTypeChange(betType: BetType) {
    if (betType === 'Mystery') {
      EventAggregator.publish('load-mystery-bet', {
        raceNumber: this.bettingInformation.raceNumber,
      })
    } else {
      window.requestAnimationFrame(() => {
        timed('BettingInformationManager::processBetTypeChange', () => {
          this.bettingInformation.mergeDone(false)
          this.reset(betType)
          this.bettingInformation.mergeDone(true)
        })
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            EventAggregator.publish('process-bet-type-completed')
          })
        })
      })
    }
  }

  private shouldResetBackToWinPlace() {
    return !this.raceInformation
      .availableBetTypes()
      .some(
        (betType: BetTypeInformation) =>
          betType.betType() === this.bettingInformation.selectedBetType().betType()
      )
  }

  public reset(betType: BetType, preserveSelections = false) {
    timed('BettingInformationManager::reset', () => {
      if (!preserveSelections) {
        this.bettingInformation.clearAll()
      }
      this.bettingInformation.selectedBetType(
        BetTypeInformationBuilder.fromUnTyped(betType).build()
      )
      this.bettingInformation.selections.setUpForLegs(this.raceInformation, preserveSelections)
      this.bettingInformation.isFixed(false)
      if (!preserveSelections) {
        this.bettingInformation.setUpFields()
        this.bettingInformation.sizeSameAsToBetType()
      }
    })
  }

  private raceInformation!: ObservableRacePage
  private bettingInformation!: BettingInformation
}
